import { Blac, Cubit } from "blac-next";
import reportErrorSentry from "src/lib/reportErrorSentry";
import type { ChartVisibilityPreference } from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import UserPreferencesCubit from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import WithingsFeaturesCubit, {
  WithingsFeatureMode
} from "src/state/WithingsFeaturesCubit/WithingsFeaturesCubit";

export interface HealthDataSettingsState {
  loading: boolean;
}

export default class HealthDataSettingsCubit extends Cubit<HealthDataSettingsState> {
  userPreferences = Blac.getBloc(UserPreferencesCubit);
  withingsFeatures = Blac.getBloc(WithingsFeaturesCubit);

  constructor() {
    super({ loading: false });
  }

  load = async (): Promise<void> => {
    this.patch({ loading: true });

    try {
      await Promise.all([
        this.userPreferences.loadUserPreferences(),
        this.withingsFeatures.load()
      ]);
    } catch (error) {
      reportErrorSentry(error);
    } finally {
      this.patch({ loading: false });
    }
  };

  isChartVisible = (preference: ChartVisibilityPreference): boolean =>
    this.userPreferences.isChartVisible(preference);

  /**
   * Both writes below apply the new value before the request leaves and roll it
   * back if it fails, so a row always follows the tap that flipped it. Nothing
   * waits on a load: a switch that ignores taps while preferences resolve reads
   * as broken, and the value on screen is the one that gets written either way.
   */
  toggleChartVisibility = async (
    preference: ChartVisibilityPreference
  ): Promise<void> => {
    await this.userPreferences.updateUserPreferencesOptimistic({
      [preference]: !this.isChartVisible(preference)
    });
  };

  /**
   * The member-facing toggle is the inverse of the Withings feature: Eyes Closed
   * mode means the scale withholds the reading, so "Show Measurements on Scale"
   * is on exactly when EYES_CLOSED is off.
   */
  get showMeasurementsOnScale(): boolean {
    return !this.withingsFeatures.isEnabled(WithingsFeatureMode.EYES_CLOSED);
  }

  toggleMeasurementsOnScale = async (): Promise<void> => {
    try {
      await this.withingsFeatures.setFeature(
        WithingsFeatureMode.EYES_CLOSED,
        this.showMeasurementsOnScale
      );
    } catch (error) {
      reportErrorSentry(error);
    }
  };

  get showBodyCompositionRow(): boolean {
    return this.withingsFeatures.bodyCompositionAvailable;
  }

  get showDevicesSection(): boolean {
    return this.withingsFeatures.eyesClosedAvailable;
  }
}
